/**
 * Sales-pipeline funnel - SharePoint-only.
 *
 * Renders the full lifecycle analytics off the SharePoint "Medicys
 * Schedule of Work" tracker (via a 15-min Mongo cache). Clicking
 * QUOTED / COMMISSIONED on the funnel strip swaps the book shown
 * below. Commissioned drill-down filters (?team=…&client=…) live in
 * the URL so filtered views are shareable - arriving with either
 * param opens the Commissioned Book directly.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import StaffShell from "../components/staff/StaffShell";
import PipelineAnalytics from "../components/staff/PipelineAnalytics";
import QuotedBook from "../components/staff/QuotedBook";
import CommissionedBook from "../components/staff/CommissionedBook";
import SowBook from "../components/staff/SowBook";
import BriefedBook from "../components/staff/BriefedBook";
import InFieldBook from "../components/staff/InFieldBook";
import InvoicedBook from "../components/staff/InvoicedBook";

const BOOK_IDS = { quoted: "quoted-book", commissioned: "commissioned-book", sow: "sow-book", briefed: "briefed-book", infield: "infield-book", invoiced: "invoiced-book" };

export default function Pipeline() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [book, setBook] = useState(() => {
    const b = searchParams.get("book");
    if (BOOK_IDS[b]) return b;
    return searchParams.get("team") || searchParams.get("client") ? "commissioned" : "quoted";
  });

  const selectBook = (b) => {
    setBook(b);
    // Manual tile switch starts a fresh view - drop stale drill params.
    if (searchParams.get("team") || searchParams.get("client") || searchParams.get("book")) {
      const next = new URLSearchParams(searchParams);
      next.delete("team");
      next.delete("client");
      next.delete("book");
      setSearchParams(next, { replace: true });
    }
    setTimeout(() => {
      document.getElementById(BOOK_IDS[b])?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  return (
    <StaffShell max="wide" bodyClass="!gap-6 !pt-8 !pb-12">
      {/* ---- header strip ----------------------------------------- */}
      <header
        data-testid="pipeline-header"
        className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
      >
        <div>
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-1">
            Sales pipeline
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 leading-tight">
            The funnel
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl leading-relaxed">
            Live view of every deal in the SharePoint Schedule of Work - including
            the full LOST and CANCELLED back-catalogue.
          </p>
        </div>
      </header>

      {/* ---- SharePoint funnel analytics --------------------------- */}
      <div data-testid="pipeline-layout" className="w-full">
        <PipelineAnalytics activeBook={book} onSelectBook={selectBook} />
        {book === "quoted" && <QuotedBook />}
        {book === "commissioned" && <CommissionedBook />}
        {book === "sow" && <SowBook />}
        {book === "briefed" && <BriefedBook />}
        {book === "infield" && <InFieldBook />}
        {book === "invoiced" && <InvoicedBook />}
      </div>
    </StaffShell>
  );
}
